import { scrollToElement } from "@/lib/scroll";
import { routing } from "@/i18n/routing";
import type { Form, Page } from "@/payload-types";
import type { FormInputSchemaParams, FormField } from "@/lib/types/form/form-field";

export function resolveFormRedirectHref(form: Form, locale: string): string | null {
	if (form.confirmationType !== "redirect" || !form.redirect) {
		return null;
	}

	const { type, url, reference } = form.redirect;

	if (type === "custom" && typeof url === "string") {
		const t = url.trim();
		if (t.length === 0) return null;
		if (/^https?:\/\//i.test(t) || t.startsWith("//")) return t;
		return t;
	}

	if (type === "reference" && reference?.relationTo === "pages") {
		const v = reference.value;
		if (typeof v === "object" && v !== null && "slug" in v) {
			const slug = (v as Page).slug;
			if (typeof slug === "string" && slug.length > 0) {
				return slug;
			}
		}
	}

	return null;
}

export function performFormSubmitConfirmationRedirect(form: Form, locale: string): boolean {
	const href = resolveFormRedirectHref(form, locale);
	if (!href || typeof window === "undefined") return false;
	window.location.assign(href);
	return true;
}

export function buildFormInputSchemaParams(block: FormField): FormInputSchemaParams {
	const content = (block.content ?? {}) as Record<string, unknown>;
	const settings = "settings" in block ? ((block.settings ?? {}) as Record<string, unknown>) : {};
	const options = ("options" in block ? (block.options ?? []) : []) as Array<{ value?: string | null }>;

	return {
		...content,
		...settings,
		options,
	} as FormInputSchemaParams;
}

export function getFirstFieldErrorMessage(errors: unknown[]): string | undefined {
	for (const error of errors) {
		if (typeof error === "string" && error.trim().length > 0) {
			return error;
		}
	}

	return undefined;
}

export function scrollToFirstFormError(formElement: HTMLFormElement): void {
	window.requestAnimationFrame(() => {
		const firstError = formElement.querySelector("[data-field-error='true']") as HTMLElement | null;
		if (!firstError) return;

		const fieldContainer = firstError.parentElement;
		const focusTarget = fieldContainer?.querySelector(
			"input:not([type='hidden']), textarea, [role='combobox'], [role='checkbox'], button, [tabindex]:not([tabindex='-1'])",
		) as HTMLElement | null;

		if (focusTarget) {
			focusTarget.focus({ preventScroll: true });
			if (focusTarget.id) {
				scrollToElement(`#${focusTarget.id}`);
				return;
			}
			focusTarget.id = "temp-scroll-target";
			scrollToElement("#temp-scroll-target");
			return;
		}

		if (firstError.id) {
			scrollToElement(`#${firstError.id}`);
			return;
		}
		firstError.id = "temp-scroll-error";
		scrollToElement("#temp-scroll-error");
	});
}
